import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings2, X, Shield, BarChart3, Settings as SettingsIcon } from "lucide-react";

/**
 * Global cookie consent banner.
 *
 * - Renders a slide-up banner the first time the visitor lands on the site.
 * - Persists the choice in localStorage so it does not reappear.
 * - Listens to a `luxstay:open-cookie-banner` window event so the
 *   /cookies page (or footer link) can re-open the banner on demand.
 * - Three actions: Accept all / Reject all / Customize.
 * - "Customize" expands an inline panel with toggles for analytics +
 *   preferences. Essential cookies are always on (cannot be toggled).
 */

const STORAGE_KEY = "luxstay-cookie-consent-v1";

const DEFAULT_PREFS = {
  essential: true,   // Always on, cannot be turned off
  preferences: false,
  analytics: false,
  decided: false,    // Whether the visitor has made a choice yet
  ts: null,          // ISO timestamp of the decision
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_PREFS, ...parsed, essential: true };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function savePrefs(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...prefs,
      essential: true,
      decided: true,
      ts: new Date().toISOString(),
    }));
  } catch { /* localStorage might be blocked — silent fail */ }
  // Let the rest of the app react if needed (e.g. enable analytics)
  try {
    window.dispatchEvent(new CustomEvent("luxstay:cookie-prefs-updated", { detail: prefs }));
  } catch { /* */ }
}

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    const initial = loadPrefs();
    setPrefs(initial);
    if (!initial.decided) {
      // Slight delay so the banner doesn't fight the splash screen on home
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  // Allow the /cookies page (and footer) to reopen the banner on demand
  useEffect(() => {
    function reopen() {
      setPrefs(loadPrefs());
      setShowCustomize(true);
      setOpen(true);
    }
    window.addEventListener("luxstay:open-cookie-banner", reopen);
    return () => window.removeEventListener("luxstay:open-cookie-banner", reopen);
  }, []);

  function acceptAll() {
    const next = { ...prefs, essential: true, preferences: true, analytics: true };
    savePrefs(next);
    setPrefs(next);
    setOpen(false);
    setShowCustomize(false);
  }

  function rejectAll() {
    const next = { ...prefs, essential: true, preferences: false, analytics: false };
    savePrefs(next);
    setPrefs(next);
    setOpen(false);
    setShowCustomize(false);
  }

  function saveCustom() {
    savePrefs(prefs);
    setOpen(false);
    setShowCustomize(false);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-3 sm:px-6 pb-3 sm:pb-6 pointer-events-none"
         data-testid="cookie-banner">
      <div className="mx-auto max-w-3xl pointer-events-auto rounded-2xl border-2 border-[#d4af37]/40 bg-[#041f1b]/95 backdrop-blur-md shadow-[0_24px_60px_-12px_rgba(0,0,0,0.6)]">
        <div className="flex items-start gap-4 p-5 sm:p-6">
          <span className="hidden sm:grid place-items-center w-11 h-11 rounded-full bg-[#d4af37]/10 text-[#d4af37] border border-[#d4af37]/30 shrink-0">
            <Cookie size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#d4af37]">Privacidade &amp; Cookies</div>
            <h3 className="mt-1 font-display text-lg text-[#fdfbf7]">
              Cookies para uma experiência <span className="italic text-[#d4af37]">tranquila</span>
            </h3>
            <p className="mt-2 text-sm text-[#a0b2af] leading-relaxed">
              Usamos cookies essenciais para manter o site a funcionar e, com a sua permissão,
              cookies de análise para melhorarmos a plataforma. Pode aceitar tudo,
              rejeitar tudo ou personalizar. Saiba mais na{" "}
              <Link to="/cookies" className="text-[#d4af37] hover:underline">Política de Cookies</Link>.
            </p>

            {showCustomize && (
              <div className="mt-4 rounded-xl border border-[#1a4640] bg-[#062c27] p-4 space-y-3"
                   data-testid="cookie-customize-panel">
                <CategoryRow
                  Icon={Shield}
                  title="Essenciais"
                  desc="Sessão, login, segurança. Sempre activos."
                  locked
                  value={true}
                />
                <CategoryRow
                  Icon={SettingsIcon}
                  title="Preferências"
                  desc="Lembrar opções (idioma, tema, secções)."
                  testId="cookie-toggle-preferences"
                  value={prefs.preferences}
                  onChange={v => setPrefs(p => ({ ...p, preferences: v }))}
                />
                <CategoryRow
                  Icon={BarChart3}
                  title="Análise"
                  desc="Estatísticas anónimas para melhorar o site."
                  testId="cookie-toggle-analytics"
                  value={prefs.analytics}
                  onChange={v => setPrefs(p => ({ ...p, analytics: v }))}
                />
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {!showCustomize && (
                <button onClick={acceptAll}
                        data-testid="cookie-accept-all"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-xs font-semibold uppercase tracking-widest bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] hover:shadow-lg hover:shadow-[#d4af37]/40 transition-all">
                  Aceitar
                </button>
              )}
              {!showCustomize && (
                <button onClick={rejectAll}
                        data-testid="cookie-reject-all"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-xs font-semibold uppercase tracking-widest border border-[#1a4640] text-[#fdfbf7] hover:border-[#d4af37]/60 hover:text-[#d4af37] transition-all">
                  Rejeitar
                </button>
              )}
              {!showCustomize ? (
                <button onClick={() => setShowCustomize(true)}
                        data-testid="cookie-customize"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-xs font-semibold uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37] transition-colors">
                  <Settings2 size={12} /> Personalizar
                </button>
              ) : (
                <>
                  <button onClick={saveCustom}
                          data-testid="cookie-save-custom"
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-xs font-semibold uppercase tracking-widest bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] hover:shadow-lg hover:shadow-[#d4af37]/40 transition-all">
                    Guardar escolhas
                  </button>
                  <button onClick={() => setShowCustomize(false)}
                          data-testid="cookie-customize-back"
                          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-xs font-semibold uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37] transition-colors">
                    Voltar
                  </button>
                </>
              )}
            </div>
          </div>
          <button onClick={rejectAll}
                  aria-label="Fechar (rejeitar não-essenciais)"
                  data-testid="cookie-banner-close"
                  className="shrink-0 p-1.5 rounded-full text-[#a0b2af] hover:text-[#fdfbf7] hover:bg-[#1a4640]/50 transition-colors">
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ Icon, title, desc, value, onChange, locked, testId }) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid place-items-center w-8 h-8 rounded-full bg-[#d4af37]/10 text-[#d4af37] border border-[#d4af37]/30 shrink-0">
        <Icon size={14} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-[#fdfbf7]">{title}</div>
        <div className="text-xs text-[#a0b2af] leading-snug">{desc}</div>
      </div>
      {locked ? (
        <span className="px-2 py-1 rounded-full text-[9px] uppercase tracking-wider bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/30 shrink-0">
          Sempre activo
        </span>
      ) : (
        <button onClick={() => onChange(!value)}
                data-testid={testId}
                className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${value ? "bg-[#d4af37]" : "bg-[#1a4640]"}`}
                aria-pressed={value}>
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${value ? "left-[22px]" : "left-0.5"}`} />
        </button>
      )}
    </div>
  );
}
